import { useState, useEffect, useRef, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { Input } from "@/components/ui/input"
import { Search, X } from "lucide-react"
import { cn } from "@/lib/utils"

interface PagefindResult {
  id: string
  url: string
  excerpt: string
  meta: {
    title?: string
  }
  sub_results?: {
    title: string
    url: string
    excerpt: string
  }[]
}

interface PagefindSearchResult {
  results: {
    id: string
    data: () => Promise<PagefindResult>
  }[]
}

// PageFind instance loaded dynamically
let pagefind: {
  search: (query: string) => Promise<PagefindSearchResult>
  debouncedSearch: (query: string, options?: { debounceTimeoutMs?: number }) => Promise<PagefindSearchResult | null>
} | null = null

export function DocsSearch() {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<PagefindResult[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  // Load PageFind on first interaction
  const loadPagefind = useCallback(async () => {
    if (pagefind) return pagefind
    try {
      // Dynamic path prevents Vite from analyzing this import at build time
      // Pagefind files only exist after production build
      const pagefindPath = "/pagefind/pagefind.js"
      // @ts-expect-error PageFind is loaded dynamically
      pagefind = await import(/* @vite-ignore */ pagefindPath)
      return pagefind
    } catch (error) {
      console.error("Failed to load PageFind:", error)
      return null
    }
  }, [])

  // Search handler
  const handleSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([])
      setIsOpen(false)
      return
    }

    setIsLoading(true)
    const pf = await loadPagefind()
    if (!pf) {
      setIsLoading(false)
      return
    }

    try {
      const searchResult = await pf.debouncedSearch(searchQuery, { debounceTimeoutMs: 150 })
      if (!searchResult) return // Debounced, waiting for next call

      // Fetch full data for top results
      const fullResults = await Promise.all(
        searchResult.results.slice(0, 8).map(r => r.data())
      )
      setResults(fullResults)
      setIsOpen(true)
      setSelectedIndex(0)
    } catch (error) {
      console.error("Search error:", error)
    } finally {
      setIsLoading(false)
    }
  }, [loadPagefind])

  // Debounce search input
  useEffect(() => {
    handleSearch(query)
  }, [query, handleSearch])

  // Handle result click - navigate to doc and scroll to anchor
  const handleResultClick = useCallback((result: PagefindResult, subResult?: { url: string }) => {
    const url = subResult?.url || result.url

    // Convert static URL to app route
    // /static/docs/mining/index.html#heading -> /docs/mining#heading
    const match = url.match(/\/static\/docs\/([^/]+)(?:\/index\.html)?(?:\/)?(?:(#.*))?$/)
    if (match) {
      const [, docSlug, hash] = match
      // Include search query for highlighting
      const searchParam = query.trim() ? `?q=${encodeURIComponent(query.trim())}` : ""
      navigate(`/docs/${docSlug}${searchParam}${hash || ""}`)
    } else {
      // Fallback: just navigate to the URL
      navigate(url)
    }

    setQuery("")
    setResults([])
    setIsOpen(false)
  }, [navigate, query])

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        setSelectedIndex(i => Math.min(i + 1, results.length - 1))
        break
      case "ArrowUp":
        e.preventDefault()
        setSelectedIndex(i => Math.max(i - 1, 0))
        break
      case "Enter":
        e.preventDefault()
        if (results[selectedIndex]) {
          handleResultClick(results[selectedIndex])
        }
        break
      case "Escape":
        e.preventDefault()
        setIsOpen(false)
        setQuery("")
        break
    }
  }, [isOpen, results, selectedIndex, handleResultClick])

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search docs..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query && results.length > 0 && setIsOpen(true)}
          className="pl-9 pr-8 w-48 sm:w-64 h-9"
        />
        {query && (
          <button
            onClick={() => {
              setQuery("")
              setResults([])
              setIsOpen(false)
              inputRef.current?.focus()
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {isOpen && results.length > 0 && (
        <div className="absolute top-full mt-2 right-0 w-80 sm:w-96 bg-popover border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          {results.map((result, index) => (
            <button
              key={result.id}
              onClick={() => handleResultClick(result)}
              className={cn(
                "w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-accent transition-colors",
                selectedIndex === index && "bg-accent"
              )}
            >
              <div className="font-medium text-sm">
                {result.meta.title || "Untitled"}
              </div>
              {result.excerpt && (
                <div
                  className="text-xs text-muted-foreground mt-1 line-clamp-2"
                  dangerouslySetInnerHTML={{ __html: result.excerpt }}
                />
              )}
              {/* Sub-results (heading anchors) */}
              {result.sub_results && result.sub_results.length > 0 && (
                <div className="mt-2 space-y-1">
                  {result.sub_results.slice(0, 3).map((sub, subIndex) => (
                    <button
                      key={subIndex}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleResultClick(result, sub)
                      }}
                      className="block w-full text-left text-xs text-primary hover:underline pl-2 border-l-2 border-muted"
                    >
                      {sub.title}
                    </button>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Loading state */}
      {isOpen && isLoading && results.length === 0 && (
        <div className="absolute top-full mt-2 right-0 w-80 sm:w-96 bg-popover border rounded-lg shadow-lg z-50 p-4 text-center text-muted-foreground text-sm">
          Searching...
        </div>
      )}

      {/* No results */}
      {isOpen && !isLoading && query && results.length === 0 && (
        <div className="absolute top-full mt-2 right-0 w-80 sm:w-96 bg-popover border rounded-lg shadow-lg z-50 p-4 text-center text-muted-foreground text-sm">
          No results found
        </div>
      )}
    </div>
  )
}
